import { Suspense } from 'react';
import { Routes, Route, useLocation, useParams } from 'react-router-dom';
import MainLayout from './components/MainLayout';
import HomePage from './pages/HomePage';
import LangSync, { LangGate } from './components/LangSync';
import AnalyticsPageViews from './components/AnalyticsPageViews';
import { useLang } from './utils/i18n';
import { DEFAULT_LANG, langFromPath } from './utils/languages';
import {
  LANDING_PATHS,
  TEACHER_GUIDE_SLUGS,
  NAME_LIST_SLUGS,
  BLOG_POST_SLUGS,
} from './data/routeManifest';

import { lazy } from 'react';

const GalleryPage = lazy(() => import('./pages/GalleryPage'));
const HowToPage = lazy(() => import('./pages/HowToPage'));
const FaqPage = lazy(() => import('./pages/FaqPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const YesNoWheelPage = lazy(() => import('./pages/YesNoWheelPage'));
const ClassroomPage = lazy(() => import('./pages/ClassroomPage'));
const GiveawayPage = lazy(() => import('./pages/GiveawayPage'));
const EmbedPage = lazy(() => import('./pages/EmbedPage'));
const FullEmbedPage = lazy(() => import('./pages/FullEmbedPage'));
const EmbedSpinnerWheelPage = lazy(() => import('./pages/EmbedSpinnerWheelPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const TeamPickerPage = lazy(() => import('./pages/TeamPickerPage'));
const ClassesPage = lazy(() => import('./pages/ClassesPage'));
const LinkedWheelsPage = lazy(() => import('./pages/LinkedWheelsPage'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));
const GuidePage = lazy(() => import('./pages/GuidePage'));
const NamesHubPage = lazy(() => import('./pages/NamesHubPage'));
const NameListPage = lazy(() => import('./pages/NameListPage'));
const BlogIndexPage = lazy(() => import('./pages/BlogIndexPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const CompareWheelOfNamesPage = lazy(() => import('./pages/CompareWheelOfNamesPage'));
const CompareNoAccountPage = lazy(() => import('./pages/CompareNoAccountPage'));
const CompareTeamPickerWheelPage = lazy(() => import('./pages/CompareTeamPickerWheelPage'));
const NotFound = lazy(() => import('./pages/NotFound'));

/**
 * Minimal page-transition fallback — a full-height blank prevents the layout
 * from collapsing to zero while a lazy page chunk loads on navigation.
 * Not shown on initial load (HomePage is synchronous).
 */
function PageFallback() {
  return <div style={{ minHeight: '100vh' }} aria-hidden="true" />;
}

/**
 * The page routes, as paths relative to whatever parent they are mounted under.
 *
 * Declared once and mounted twice — bare for English at `/about`, and again
 * beneath `/:lang` for `/es/about`. Building the list rather than repeating it
 * means a new page cannot accidentally exist in only one of the two trees, and
 * it avoids emitting 19 x 31 Route elements on every render.
 */
function pageRoutes() {
  return (
    <>
      <Route index                          element={<HomePage />} />
      <Route path="yes-no-wheel"            element={<YesNoWheelPage />} />
      <Route path="classroom-name-picker"   element={<ClassroomPage />} />
      <Route path="giveaway-wheel"          element={<GiveawayPage />} />
      <Route path="gallery"                 element={<GalleryPage />} />
      <Route path="how-to"                  element={<HowToPage />} />
      <Route path="faq"                     element={<FaqPage />} />
      <Route path="about"                   element={<AboutPage />} />
      <Route path="contact"                 element={<ContactPage />} />
      <Route path="privacy"                 element={<PrivacyPage />} />
      <Route path="terms"                   element={<TermsPage />} />
      <Route path="classes"                 element={<ClassesPage />} />
      <Route path="linked-wheels"           element={<LinkedWheelsPage />} />
      <Route path="embed-spinner-wheel"     element={<EmbedSpinnerWheelPage />} />

      {/* Teacher resources hub. The only nested segment on the site; the
          guides sit beneath it so the URL states the relationship. */}
      <Route path="classroom/resources"     element={<ResourcesPage />} />
      {TEACHER_GUIDE_SLUGS.map(slug => (
        <Route
          key={slug}
          path={`classroom/resources/${slug}`}
          element={<GuidePage slug={slug} />}
        />
      ))}

      {/* Name lists — a browsable list plus a wheel seeded from it. */}
      <Route path="names"                   element={<NamesHubPage />} />
      {NAME_LIST_SLUGS.map(slug => (
        <Route key={slug} path={`names/${slug}`} element={<NameListPage slug={slug} />} />
      ))}

      {/* Blog. Each post is its own route so it pre-renders as a real page
          rather than being resolved client-side from a :slug param. */}
      <Route path="blog"                    element={<BlogIndexPage />} />
      {BLOG_POST_SLUGS.map(slug => (
        <Route key={slug} path={`blog/${slug}`} element={<BlogPostPage slug={slug} />} />
      ))}

      {/* Comparison / alternative pages */}
      <Route path="compare/wheel-of-names-alternative" element={<CompareWheelOfNamesPage />} />
      <Route path="compare/random-name-picker-no-account" element={<CompareNoAccountPage />} />
      <Route path="compare/teampickerwheel-alternative" element={<CompareTeamPickerWheelPage />} />

      {/* Specialised landing pages — one shared shell, per-route copy.
          See src/data/landingPages.ts. */}
      {LANDING_PATHS.map(path => (
        <Route key={path} path={path.slice(1)} element={<LandingPage path={path} />} />
      ))}
      <Route path="*" element={<NotFound />} />
    </>
  );
}

/** Validates the :lang segment before rendering the localised tree. */
function LocalisedBranch() {
  const { lang } = useParams();
  return (
    <LangGate lang={lang}>
      <MainLayout />
    </LangGate>
  );
}

export default function AppRoutes() {
  const { pathname } = useLocation();
  const { lang, loading } = useLang();
  const pathLang = langFromPath(pathname) ?? DEFAULT_LANG;
  const isEmbedPath = pathname === '/embed' || pathname.startsWith('/embed/');
  const languageReady = isEmbedPath || (lang === pathLang && !loading);

  return (
    <>
      {/* Keeps the active language in step with the URL on client navigation. */}
      <LangSync />
      <Suspense key={pathname} fallback={<PageFallback />}>
        {languageReady ? <Routes>
          {/* Embed routes — bare, no site chrome, and no language prefix. The
              original endpoint stays a compact read-only wheel; /embed/app is
              the complete editor intended for larger iframe integrations. */}
          <Route path="/embed" element={<EmbedPage />} />
          <Route path="/embed/app" element={<FullEmbedPage />} />

          {/* English — the bare paths. */}
          <Route element={<MainLayout />}>
            {/* <!-- SEO: English Team Picker guide, separately from localized tool routes. --> */}
            <Route path="team-picker" element={<TeamPickerPage />} />
            {pageRoutes()}
          </Route>

          {/* Every other language, prefixed. React Router ranks static segments
              above dynamic ones, so `/about` matches the English branch above and
              only genuinely prefixed paths like `/es/about` reach this one. */}
          <Route path=":lang" element={<LocalisedBranch />}>
            {pageRoutes()}
          </Route>
        </Routes> : <PageFallback />}
      </Suspense>
      <AnalyticsPageViews />
    </>
  );
}
